import { defineComponent as _defineComponent } from 'vue'
import { message, Modal } from "ant-design-vue"
import { computed, inject, reactive, Ref, ref, shallowRef } from "vue"

import EditorHtml from "@/components/Common/EditorHtml.vue"
import ImagesLoader from "@/components/Common/ImagesLoader/ImagesLoader.vue"
import InfiniteScrollSelect from "@/components/Common/InfiniteScrollSelect/index.vue"
import useForm from "@/composables/useForm"
import i18n from "@/i18n"
import {
  fetchProductBrands,
  fetchProductTypes,
  translateText,
  uploadImageOnCdn
} from "@/modules/offers/api/productModeration"
import type { CategoriesTreeItem } from "@/modules/offers/types/catalog"
import type {
  AttributeFormLocale,
  ProductDraftFormValues,
  ProductMainFieldsValues
} from "@/modules/offers/types/createOffer"
import type { ProductModerationBrand, ProductModerationType } from "@/modules/offers/types/offers"
import type { ProductDraftResponse } from "@/modules/offers/types/productModeration"
import router from "@/router"

import CreateBrandModal from "./CreateBrandModal.vue"

type Emits = {
  (event: "change", product: ProductDraftFormValues): void
  (event: "typeChange", type?: ProductModerationType): void
}
type Props = {
  attributeCount?: number
  productDraft?: ProductDraftResponse
  initialProductName?: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductMainFields',
  props: {
    attributeCount: { default: 0 },
    productDraft: null,
    initialProductName: null
  },
  emits: ["change", "typeChange"],
  setup(__props: any, { expose, emit }: { emit: ({
  (event: "change", product: ProductDraftFormValues): void
  (event: "typeChange", type?: ProductModerationType): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { attributeCount: number, productDraft?: ProductDraftResponse, initialProductName?: string };

const EMPTY_TRANSLATIONS = { uk: "", ru: "" }




const formRef = ref()
const form = reactive<ProductMainFieldsValues>({
  public_title_mlang: {
    uk:
      props.productDraft?.public_title_mlang?.uk ||
      (i18n.locale === "uk" ? props.initialProductName : "") ||
      "",
    ru:
      props.productDraft?.public_title_mlang?.ru ||
      (i18n.locale === "ru" ? props.initialProductName : "") ||
      ""
  },
  use_public_title: true,
  synthetic_meta: {
    category: {
      uuid: props.productDraft?.synthetic_meta?.category?.uuid || null,
      name: props.productDraft?.synthetic_meta?.category?.name || undefined
    }
  },
  model: props.productDraft?.model || "",
  type: props.productDraft?.type?.oid || undefined,
  brand: props.productDraft?.brand?.oid || undefined,
  images: props.productDraft?.images || [],
  description_mlang: {
    uk: props.productDraft?.description_mlang?.uk || "",
    ru: props.productDraft?.description_mlang?.ru || ""
  }
})

const { validateForm, isValidForm, formValidateRules } = useForm(formRef)

const formRules = computed(() => ({
  public_title_mlang: {
    uk: [
      ...formValidateRules.value.textRequiredRule,
      { max: 500, message: i18n.t("validations.max", { count: 500 }) }
    ],
    ru: [
      ...formValidateRules.value.textRequiredRule,
      { max: 500, message: i18n.t("validations.max", { count: 500 }) }
    ]
  },
  synthetic_meta: {
    category: { uuid: formValidateRules.value.textRequiredRule }
  },
  model: [{ max: 200, message: i18n.t("validations.max", { count: 200 }) }],
  brand: formValidateRules.value.textRequiredRule,
  images: [{ required: true, message: i18n.t("support.images_is_required") }],
  type: formValidateRules.value.textRequiredRule,
  description_mlang: {
    ru: [{ max: 50000, message: i18n.t("validations.max", { count: 50000 }) }],
    uk: [{ max: 50000, message: i18n.t("validations.max", { count: 50000 }) }]
  }
}))

const handleFormChanged = () => {
  const payload = {
    public_title_mlang: form.public_title_mlang,
    model: form.model || null,
    brand: form.brand || null,
    description_mlang: form.description_mlang
  }

  emit("change", payload)
}

const categoriesTree = inject<Ref<Array<CategoriesTreeItem>>>(
  "categoriesTree",
  shallowRef<Array<CategoriesTreeItem>>([])
)

const categoriesTreeCustom = computed(() => [
  ...categoriesTree.value,
  {
    key: "add_new",
    value: null,
    disabled: true,
    scopedSlots: { title: "createCategoryRenderer" }
  }
])

const handleCategoryChange = (uuid: string, name: string | Array<string>) => {
  if (props.attributeCount) {
    Modal.confirm({
      title: i18n.t("offers.change_product_type_warnign"),
      onOk: () => onCategoryChange(uuid, name)
    })
  } else {
    onCategoryChange(uuid, name)
  }
}
const onCategoryChange = async (uuid: string, name: string | Array<string>) => {
  const categoryName = Array.isArray(name) ? name.join("") : name

  form.synthetic_meta.category = {
    uuid,
    name: {
      uk: categoryName,
      ru: categoryName,
      en: categoryName
    }
  }

  try {
    const { data } = await fetchProductTypes({
      queryParams: {
        related_category: uuid,
        pageSize: 1
      }
    })

    if (data.count === 1) {
      typeSelectRef.value.onResetOptions()
      defaultTypeOptions.value = data.results

      const [type] = data.results
      form.type = type.oid

      emit("typeChange", type)
      formRef.value.clearValidate("type")
    } else {
      resetType()
    }
  } catch {
    resetType()
  }

  emitCategoryTypeChange()
}
const handleCreateNewCategory = () => {
  const route = router.resolve({ name: "support", query: { openCategoryCreation: "true" } })
  window.open(route.href)
}
const emitCategoryTypeChange = () => {
  emit("change", { synthetic_meta: form.synthetic_meta, type: form.type || null })
}

const typeSelectRef = ref()
const defaultTypeOptions = ref<Array<ProductModerationType>>(
  props.productDraft?.type ? [props.productDraft.type] : []
)

const resetType = () => {
  form.type = undefined
  defaultTypeOptions.value.length = 0
  typeSelectRef.value.onResetOptions()
  emit("typeChange")
}
const handleTypeChange = (value: string) => {
  if (props.attributeCount) {
    Modal.confirm({
      title: i18n.t("offers.change_product_type_warnign"),
      onOk: () => handleSelectType(value)
    })
  } else {
    handleSelectType(value)
  }
}
const handleSelectType = (value?: string) => {
  form.type = value
  const typeObject: ProductModerationType | undefined = typeSelectRef.value.selectOptions?.find(
    ({ oid }) => oid === value
  )
  emit("typeChange", typeObject)
  emitCategoryTypeChange()
}
const handleCreateNewType = () => {
  const route = router.resolve({ name: "support", query: { openTypeCreation: "true" } })
  window.open(route.href)
}

const brandOptions = ref<Array<ProductModerationBrand>>(
  props.productDraft?.brand ? [props.productDraft.brand] : []
)

const handleBrandChange = (value: string) => {
  form.brand = value

  if (value) formRef.value.clearValidate("brand")

  emit("change", { brand: value || null })
}

const showCreateBrandModal = shallowRef<boolean>(false)

const onOpenBrandModal = () => {
  showCreateBrandModal.value = true
}
const onCloseBrandModal = () => {
  showCreateBrandModal.value = false
}
const handleBrandCreated = (brand: ProductModerationBrand) => {
  brandOptions.value = [brand]
  handleBrandChange(brand.oid)
  onCloseBrandModal()
}

const isUploadingImage = shallowRef(false)

const uploadImage = async (file: File) => {
  const payload = new FormData()
  payload.append("file", file)
  const { data } = await uploadImageOnCdn(payload)
  return { filename: data.filename, on_cdn: true }
}
const handleAddImage = async (file: File) => {
  try {
    isUploadingImage.value = true

    form.images.push(await uploadImage(file))
    formRef.value.clearValidate("images")

    emit("change", { images: form.images })
  } catch {
    message.error(i18n.t("common.error") as string)
  } finally {
    isUploadingImage.value = false
  }
}
const handleChangeImage = async ({ index, file }: { index: number; file: File }) => {
  try {
    isUploadingImage.value = true

    const image = await uploadImage(file)
    form.images.splice(index, 1, image)

    emit("change", { images: form.images })
  } catch (error) {
    message.error(i18n.t("common.error") as string)
  } finally {
    isUploadingImage.value = false
  }
}
const onRemoveImage = (index: number) => {
  if (index >= form.images.length) return

  form.images.splice(index, 1)
  emit("change", { images: form.images })
}
const onSortImages = ({ oldIndex, newIndex }: Record<string, number>) => {
  form.images.splice(newIndex, 0, form.images.splice(oldIndex, 1)[0])
  emit("change", { images: form.images })
}

const isTranslating = ref(false)
const editorRef = ref()
const editorRuRef = ref()

const translateField = async (
  field: "public_title_mlang" | "description_mlang",
  sourceLocale: AttributeFormLocale
) => {
  const text = form[field][sourceLocale]

  if (!text || isTranslating.value) return

  const targetLocale = sourceLocale === "uk" ? "ru" : "uk"
  isTranslating.value = true
  try {
    const { data } = await translateText({ text, target_locale: targetLocale })
    form[field][targetLocale] = data.text

    if (field === "description_mlang") {
      const editor = targetLocale === "uk" ? editorRef : editorRuRef
      editor.value.setNewContent(data.text.replaceAll("\n", ""))
    }

    handleFormChanged()
  } finally {
    isTranslating.value = false
  }
}
const getFormValues = async () => {
  await validateForm()

  if (!isValidForm.value) {
    message.error({
      content: i18n.t("offers.offer_form_validate_error") as string,
      key: "form-validate"
    })
    return null
  }

  return form
}
const resetForm = async () => {
  emit("typeChange")
  await formRef.value.resetFields()
  resetFormFields()
}
const resetFormFields = () => {
  form.public_title_mlang = { ...EMPTY_TRANSLATIONS }
  form.description_mlang = { ...EMPTY_TRANSLATIONS }
  form.synthetic_meta.category = { name: undefined, uuid: undefined }
  form.model = undefined
  form.type = undefined
  form.brand = undefined
  form.images = []
}

expose({
  getFormValues,
  getDraftValues: () => ({
    public_title_mlang: form.public_title_mlang,
    model: form.model || null,
    brand: form.brand || null,
    type: form.type || null,
    description_mlang: form.description_mlang,
    synthetic_meta: form.synthetic_meta,
    images: form.images
  }),
  resetForm
})

return { __sfc: true,EMPTY_TRANSLATIONS, emit, props, formRef, form, validateForm, isValidForm, formValidateRules, formRules, handleFormChanged, categoriesTree, categoriesTreeCustom, handleCategoryChange, onCategoryChange, handleCreateNewCategory, emitCategoryTypeChange, typeSelectRef, defaultTypeOptions, resetType, handleTypeChange, handleSelectType, handleCreateNewType, brandOptions, handleBrandChange, showCreateBrandModal, onOpenBrandModal, onCloseBrandModal, handleBrandCreated, isUploadingImage, uploadImage, handleAddImage, handleChangeImage, onRemoveImage, onSortImages, isTranslating, editorRef, editorRuRef, translateField, getFormValues, resetForm, resetFormFields, EditorHtml, ImagesLoader, InfiniteScrollSelect, fetchProductBrands, fetchProductTypes, CreateBrandModal }
}

})